import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, formatCLP } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  BadgeCheck, MapPin, Clock, ChevronLeft, Star, Lock,
  Shield, Upload, FileText, CheckCircle2, Download, X, Check, Edit3,
} from "lucide-react";
import OJVTracker from "@/components/OJVTracker";
import PaymentSummary from "@/components/PaymentSummary";
import ManualTransferForm from "@/components/ManualTransferForm";

const TASK_TYPE_LABEL = {
  audiencia: "Audiencia",
  mediacion: "Mediación",
  escrito: "Escrito",
  tramite: "Trámite",
  gestion: "Gestión",
  otro: "Otro",
};

export default function DelegationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [bidForm, setBidForm] = useState({ message: "", proposed_fee_clp: "" });
  const [showPayModal, setShowPayModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectingBid, setRejectingBid] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/delegations/${id}`);
      setData(d);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-refresh every 10s so flow updates appear live without manual reload
  useEffect(() => {
    const tick = async () => {
      try {
        const { data: d } = await api.get(`/delegations/${id}`);
        setData(d);
      } catch (e) { console.debug("delegation poll failed:", e?.response?.status || e.message); }
    };
    const handle = setInterval(tick, 10000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <div className="container-app py-16"><div className="panel p-8 text-[hsl(var(--destructive))]" data-testid="detail-error">{error}</div></div>;
  if (!data) return <div className="container-app py-16 text-muted-foreground" data-testid="detail-loading">Cargando…</div>;

  const d = data.delegation;
  const isOwner = user?.id === d.owner_id;
  const isAssigned = user?.id === d.assigned_to;
  const canBid = user?.role === "lawyer" && !isOwner && d.status === "open";

  const placeBid = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/delegations/${id}/bid`, {
        delegation_id: id,
        message: bidForm.message,
        proposed_fee_clp: bidForm.proposed_fee_clp ? Number(bidForm.proposed_fee_clp) : null,
      });
      setBidForm({ message: "", proposed_fee_clp: "" });
      load();
    } catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const assign = async (bidId) => {
    if (!window.confirm("¿Asignar a este colega? Después podrás pagar y subir los antecedentes.")) return;
    try { await api.post(`/delegations/${id}/assign/${bidId}`); load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const submitBidReject = async ({ counter_fee_clp }) => {
    if (!rejectingBid) return;
    try {
      const body = counter_fee_clp ? { counter_fee_clp } : {};
      await api.post(`/delegations/${id}/bids/${rejectingBid.id}/reject`, body);
      setRejectingBid(null);
      load();
    } catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta delegación?")) return;
    try { await api.post(`/delegations/${id}/cancel`); load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  // Status helpers
  const stepIndex = ({
    open: 0, assigned: 1, in_progress: 2, awaiting_approval: 3, completed: 4, cancelled: -1
  })[d.status] ?? 0;

  return (
    <div className="container-app py-12" data-testid="delegation-detail">
      <button onClick={()=>nav(-1)} className="text-sm inline-flex items-center text-muted-foreground hover:text-foreground" data-testid="back-button"><ChevronLeft size={14}/>Volver</button>

      {/* Steps */}
      <ProgressSteps current={stepIndex} cancelled={d.status === "cancelled"}/>

      <div className="mt-8 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill capitalize">{TASK_TYPE_LABEL[d.task_type] || d.task_type}</span>
            <StatusPill status={d.status}/>
            {d.owner_verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Titular verificado</span>}
            <span className="pill badge-cyan"><Lock size={12}/>Operación anónima</span>
          </div>
          <h1 className="serif text-5xl mt-4 leading-tight">{d.title}</h1>

          <dl className="mt-8 grid sm:grid-cols-2 gap-px bg-[hsl(var(--border))] border rounded-lg overflow-hidden" style={{ borderColor: "hsl(var(--border))" }}>
            <Item label="Tribunal" value={d.court}/>
            <Item label="Causa / RIT" value={d.case_number || "—"}/>
            <Item label="Región" value={d.region}/>
            <Item label="Comuna" value={d.comuna}/>
            <Item label="Dirección" value={d.address || "—"}/>
            <Item label="Fecha" value={`${d.date} · ${d.time}`}/>
            <Item label="Honorario" value={formatCLP(d.fee_clp)}/>
            <Item label="Titular" value={isOwner ? `${d.owner_name} (tú)` : "Identidad reservada"}/>
          </dl>

          {d.notes && (
            <div className="mt-8 panel p-6">
              <div className="label-eyebrow">Notas</div>
              <p className="mt-3 text-foreground/90 leading-relaxed">{d.notes}</p>
            </div>
          )}

          {/* Bids list (only when open) */}
          {d.status === "open" && (
            <>
              <h2 className="serif text-3xl mt-12">Postulaciones ({data.bids.length})</h2>
              <ul className="mt-4 space-y-3" data-testid="bids-list">
                {data.bids.length === 0 && <li className="panel p-6 text-muted-foreground">Aún no hay postulaciones.</li>}
                {data.bids.map((b) => {
                  const isMyBid = b.lawyer_id === user?.id;
                  const fee = b.current_fee_clp ?? b.proposed_fee_clp;
                  const myRoleKey = isOwner ? "owner" : "bidder";
                  const counterUsed = (b.counter_used || {})[myRoleKey] === true;
                  const awaitingMe = (
                    (isOwner && (b.awaiting_decision_from || "owner") === "owner") ||
                    (isMyBid && b.awaiting_decision_from === "bidder")
                  );
                  const isClosed = b.status === "rejected" || b.status === "accepted";
                  return (
                    <li key={b.id} className={`panel p-5 ${isClosed ? "opacity-60" : ""}`} data-testid={`bid-${b.id}`}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="serif text-xl">{isMyBid ? `${b.lawyer_name} (tú)` : b.lawyer_name}</span>
                            {b.lawyer_verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                            {b.lawyer_reputation > 0 && <span className="pill"><Star size={12}/>{b.lawyer_reputation}</span>}
                            {b.status === "countered_by_owner" && <span className="pill badge-pending">Titular contraofertó</span>}
                            {b.status === "countered_by_bidder" && <span className="pill badge-pending">Postulante contraofertó</span>}
                            {b.status === "rejected" && <span className="pill">Rechazada</span>}
                          </div>
                          {b.message && <p className="mt-2 text-sm text-foreground/80">{b.message}</p>}
                          {(b.fee_history || []).length > 1 && (
                            <ol className="mt-2 text-xs text-muted-foreground space-y-0.5">
                              {(b.fee_history || []).map((h, i) => (
                                <li key={`${h.at}-${h.by_role}`}>{h.by_role === "owner" ? "Titular" : "Postulante"}: <strong>{formatCLP(h.amount)}</strong></li>
                              ))}
                            </ol>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="serif text-2xl">{formatCLP(fee)}</div>
                          {!isClosed && awaitingMe && (
                            <div className="mt-2 flex gap-2 justify-end">
                              <button onClick={()=>assign(b.id)} className="btn-primary text-sm py-2" data-testid={`accept-${b.id}`}>
                                <Check size={14} className="mr-1"/>Aceptar
                              </button>
                              <button onClick={()=>setRejectingBid(b)} className="btn-ghost text-sm py-2" data-testid={`reject-${b.id}`}>
                                <X size={14} className="mr-1"/>Rechazar
                              </button>
                            </div>
                          )}
                          {!isClosed && !awaitingMe && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              Esperando a {b.awaiting_decision_from === "owner" ? "titular" : "postulante"}…
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {/* Files: case + power (visible to titular & assigned throughout the workflow) */}
          {(isOwner || isAssigned) && ["in_progress", "awaiting_approval", "completed"].includes(d.status) && (
            <FilesSection delegation={d} reload={load} role={isOwner ? "titular" : "delegado"}/>
          )}
        </div>

        {/* Sidebar workflow */}
        <aside className="space-y-6">
          {/* OWNER actions */}
          {isOwner && d.status === "assigned" && d.payment_status === "pending_payment" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--primary))]">Paso 1 · Pago Protegido Colega Link</div>
              <h3 className="serif text-2xl mt-2">Activa la delegación</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {d.assigned_to_name} aceptó cubrir tu audiencia. Confirma el Pago Protegido para desbloquear la subida de antecedentes.
                El dinero queda retenido hasta que apruebes el comprobante.
              </p>
              <button onClick={()=>setShowPayModal(true)} className="btn-primary w-full mt-5" data-testid="pay-btn">
                <Shield size={16} className="mr-2"/> Confirmar Pago Protegido
              </button>
              <p className="text-xs text-muted-foreground mt-3 text-center">Transferencia manual · Validación por administración</p>
            </div>
          )}

          {isOwner && d.payment_status === "pending_admin_validation" && (
            <div className="panel-glow p-6 border-amber-400/30" data-testid="pending-validation-card">
              <div className="label-eyebrow text-amber-300">Comprobante recibido</div>
              <h3 className="serif text-2xl mt-2">Pendiente de validación</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Tu comprobante llegó al administrador. Cuando lo valide, la delegación pasa a Pago Protegido activo y tu colega podrá empezar la gestión.
              </p>
              <span className="pill badge-pending mt-4 inline-flex">Pendiente de validación</span>
            </div>
          )}

          {isOwner && d.status === "awaiting_approval" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--accent))]">Paso 4 · Liberar pago</div>
              <h3 className="serif text-2xl mt-2">Comprobante recibido</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Revisa el comprobante subido por tu colega y, si está conforme, libera el pago retenido.
              </p>
              <button onClick={()=>setShowApproveModal(true)} className="btn-primary w-full mt-5" data-testid="approve-btn">
                <CheckCircle2 size={16} className="mr-2"/> Aprobar y liberar pago
              </button>
            </div>
          )}

          {/* DELEGATED LAWYER actions */}
          {isAssigned && d.status === "in_progress" && d.payment_status === "in_escrow" && (
            <div className="panel-glow p-6">
              <div className="label-eyebrow text-[hsl(var(--primary))]">Paso 5 · Comprobante de gestión</div>
              <h3 className="serif text-2xl mt-2">Tu turno</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Una vez presentado el escrito en OJV (pasos 1-3), realiza la gestión y luego sube el comprobante.
                El titular debe aprobarlo para que se libere tu honorario.
              </p>
              <ProofUpload delegationId={id} onUploaded={load}/>
            </div>
          )}

          {/* OJV tracker — visible to both parties when escrow is paid */}
          {d.assigned_to && ["in_escrow", "released"].includes(d.payment_status) && (isOwner || isAssigned) && (
            <OJVTracker
              delegationId={id}
              isOwner={isOwner}
              isAssigned={isAssigned}
              paymentStatus={d.payment_status}
              onUpdate={load}
            />
          )}

          {/* Status snapshots */}
          {d.assigned_to_name && (
            <div className="panel p-6">
              <div className="label-eyebrow">{isOwner ? "Asignado a" : isAssigned ? "Eres el colega asignado" : "Estado"}</div>
              <div className="serif text-2xl mt-2">{isOwner ? d.assigned_to_name : (isAssigned ? "Tú" : "Asignado")}</div>
              <div className="text-sm text-muted-foreground mt-1">Honorario acordado: {formatCLP(d.agreed_fee_clp || d.fee_clp)}</div>
              {d.payment_status === "in_escrow" && <span className="pill badge-cyan mt-3 inline-flex">Pago Protegido activo</span>}
              {d.payment_status === "released" && <span className="pill badge-verified mt-3 inline-flex">Pago liberado al colega</span>}
              {d.payment_status === "pending_payment" && isOwner && <span className="pill badge-pending mt-3 inline-flex">Pago pendiente</span>}
            </div>
          )}

          {/* MATCH DATA: legal identity of the counterpart — unlocked after escrow payment */}
          {d.match_data && (isOwner || isAssigned) && (
            <MatchDataCard
              owner={d.match_data.owner}
              assignee={d.match_data.assignee}
              isOwner={isOwner}
              delegaPoderUrl={d.match_data.delega_poder_url}
            />
          )}
          {d.assigned_to && !["in_escrow", "released"].includes(d.payment_status) && (isOwner || isAssigned) && (
            <div className="panel p-5 border-dashed" data-testid="match-data-locked">
              <div className="label-eyebrow text-amber-300">🔒 Datos del colega bloqueados</div>
              <p className="text-sm text-muted-foreground mt-2">
                Una vez confirmado el Pago Protegido (incluido el cargo Colega Link), se libera el RUT,
                domicilio profesional y la <strong>minuta automática «Delega Poder»</strong> ya rellenada.
              </p>
            </div>
          )}

          {canBid && (
            <form onSubmit={placeBid} className="panel p-6" data-testid="bid-form">
              <div className="label-eyebrow">Postular</div>
              <h3 className="serif text-2xl mt-2">Cubrir esta delegación</h3>
              <textarea className="field mt-4" rows={3} placeholder="Mensaje breve (opcional)" value={bidForm.message} onChange={(e)=>setBidForm({...bidForm, message:e.target.value})} data-testid="bid-message"/>
              <input className="field mt-3" type="number" placeholder={`Honorario propuesto (${d.fee_clp})`} value={bidForm.proposed_fee_clp} onChange={(e)=>setBidForm({...bidForm, proposed_fee_clp:e.target.value})} data-testid="bid-fee"/>
              <button className="btn-primary w-full mt-4" data-testid="bid-submit">Enviar postulación</button>
            </form>
          )}

          {isOwner && d.status === "open" && (
            <button onClick={() => nav(`/app/delegaciones/${id}/editar`)} className="btn-ghost w-full" data-testid="edit-audiencia-btn">
              <Edit3 size={14} className="mr-1.5"/> Editar audiencia
            </button>
          )}

          {isOwner && (d.status === "open" || d.status === "assigned") && (
            <button onClick={cancel} className="btn-danger w-full" data-testid="cancel-btn">
              <X size={14} className="mr-1.5"/> Cancelar delegación
            </button>
          )}
        </aside>
      </div>

      {showPayModal && (
        <PayModal delegationId={id} onClose={()=>setShowPayModal(false)} onPaid={()=>{ setShowPayModal(false); load(); }}/>
      )}
      {showApproveModal && (
        <ApproveModal delegationId={id} onClose={()=>setShowApproveModal(false)} onApproved={()=>{ setShowApproveModal(false); load(); }}/>
      )}
      {rejectingBid && (
        <BidRejectChoiceModal
          bid={rejectingBid}
          isOwner={isOwner}
          onClose={()=>setRejectingBid(null)}
          onSubmit={submitBidReject}
        />
      )}
    </div>
  );
}

function MatchDataCard({ owner, assignee, isOwner, delegaPoderUrl }) {
  const me = isOwner ? owner : assignee;
  const counterpart = isOwner ? assignee : owner;
  const counterpartLabel = isOwner ? "Colega delegado (asignado)" : "Colega delegante (titular)";
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const fullUrl = `${apiBase}${delegaPoderUrl}`;
  return (
    <div className="panel-glow p-6" data-testid="match-data-card">
      <div className="label-eyebrow text-[hsl(var(--primary))]">🔓 Datos del match liberados</div>
      <h3 className="serif text-2xl mt-2">Datos legales para «delega poder»</h3>

      <div className="mt-4 panel p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{counterpartLabel}</div>
        <div className="serif text-lg mt-1">{counterpart?.name}</div>
        <Row label="RUT" value={counterpart?.rut || "(no informado)"} testid="cp-rut"/>
        <Row label="Domicilio profesional" value={counterpart?.domicilio_profesional || "(no informado)"} testid="cp-address"/>
        <Row label="N° Registro PJUD" value={counterpart?.registry_number || "—"} testid="cp-registry"/>
        <Row label="Email" value={counterpart?.email || "—"}/>
        <Row label="Teléfono" value={counterpart?.phone || "—"}/>
      </div>

      <div className="mt-4 text-xs text-muted-foreground panel p-3">
        Tus datos visibles a la contraparte:
        <div className="mt-1"><strong>{me?.name}</strong> · RUT {me?.rut || "(falta)"} · {me?.domicilio_profesional || "(falta domicilio)"}</div>
        {(!me?.rut || !me?.domicilio_profesional) && (
          <a href="/app/perfil" className="link-underline mt-1 inline-block text-amber-300">
            ⚠ Completa tu RUT y domicilio en tu perfil
          </a>
        )}
      </div>

      <a
        href={fullUrl}
        target="_blank"
        rel="noreferrer"
        className="btn-primary w-full mt-5"
        data-testid="delega-poder-link"
      >
        <Download size={14} className="mr-1.5"/>Generar minuta «Delega Poder»
      </a>
      <p className="text-xs text-muted-foreground mt-2 text-center">
        Abre la minuta lista para imprimir / guardar como PDF. Revisa los datos antes de firmar.
      </p>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between text-sm mt-2" data-testid={testid}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function BidRejectChoiceModal({ bid, isOwner, onClose, onSubmit }) {
  const roleKey = isOwner ? "owner" : "bidder";
  const counterUsed = (bid.counter_used || {})[roleKey] === true;
  const currentFee = bid.current_fee_clp ?? bid.proposed_fee_clp;
  const [mode, setMode] = useState("choose");
  const [fee, setFee] = useState(currentFee);
  const [busy, setBusy] = useState(false);
  const submitCounter = async () => {
    const n = Number(fee);
    if (!n || n <= 0) { alert("Ingresa un monto válido."); return; }
    setBusy(true);
    try { await onSubmit({ counter_fee_clp: n }); } finally { setBusy(false); }
  };
  const submitFinal = async () => {
    setBusy(true);
    try { await onSubmit({}); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="bid-reject-choice-modal">
      <div className="label-eyebrow text-[hsl(var(--destructive))]">Rechazar postulación</div>
      <h2 className="serif text-3xl mt-2">¿Qué deseas hacer?</h2>
      {mode === "choose" && (
        <div className="mt-6 space-y-3">
          <button
            onClick={()=>setMode("counter")}
            disabled={counterUsed}
            className={`w-full text-left panel p-4 hover:border-[hsl(var(--primary))]/60 transition ${counterUsed ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="bid-reject-option-counter"
          >
            <div className="serif text-lg">Proponer nuevo monto de honorario</div>
            <div className="text-xs text-muted-foreground mt-1">
              {counterUsed
                ? "Ya usaste tu intento de contrapropuesta en esta postulación."
                : "Tienes 1 intento. La otra parte podrá aceptar o rechazar."}
            </div>
          </button>
          <button
            onClick={()=>setMode("confirm_final")}
            className="w-full text-left panel p-4 hover:border-[hsl(var(--destructive))]/60 transition"
            data-testid="bid-reject-option-final"
          >
            <div className="serif text-lg">Rechazar definitivamente</div>
            <div className="text-xs text-muted-foreground mt-1">
              La postulación se descarta. La delegación queda abierta inmediatamente para nuevas postulaciones.
            </div>
          </button>
        </div>
      )}
      {mode === "counter" && (
        <div className="mt-6">
          <div className="label-eyebrow">Nuevo honorario propuesto (CLP)</div>
          <input
            className="field mt-2"
            type="number"
            min={1000}
            step={1000}
            value={fee}
            onChange={(e)=>setFee(e.target.value)}
            data-testid="bid-reject-counter-input"
          />
          <div className="mt-4 flex gap-3">
            <button onClick={submitCounter} disabled={busy} className="btn-primary flex-1" data-testid="bid-reject-counter-submit">
              {busy ? "Enviando…" : "Enviar contrapropuesta"}
            </button>
            <button onClick={()=>setMode("choose")} className="btn-ghost">Atrás</button>
          </div>
        </div>
      )}
      {mode === "confirm_final" && (
        <div className="mt-6">
          <p className="text-sm">Esta acción no se puede deshacer. La postulación se descartará.</p>
          <div className="mt-4 flex gap-3">
            <button onClick={submitFinal} disabled={busy} className="btn-danger flex-1" data-testid="bid-reject-final-submit">
              {busy ? "Procesando…" : "Sí, rechazar definitivamente"}
            </button>
            <button onClick={()=>setMode("choose")} className="btn-ghost">Atrás</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Item({ label, value }) {
  return (
    <div className="bg-[hsl(var(--card))] p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1.5 text-base">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    open: ["badge-cyan", "Abierta"],
    assigned: ["badge-pending", "Asignada · Pago Protegido pendiente"],
    in_progress: ["badge-cyan", "En curso"],
    awaiting_approval: ["badge-pending", "Esperando aprobación"],
    completed: ["badge-verified", "Completada"],
    cancelled: ["", "Cancelada"],
  };
  const [cls, label] = map[status] || ["", status];
  return <span className={`pill ${cls}`}>{label}</span>;
}

function ProgressSteps({ current, cancelled }) {
  const steps = ["Abierta", "Asignada · Pago Protegido", "Antecedentes y gestión", "Comprobante", "Pago liberado"];
  if (cancelled) return <div className="mt-8 pill badge-pending" data-testid="status-cancelled">Esta delegación fue cancelada</div>;
  return (
    <ol className="mt-8 flex items-center gap-3 sm:gap-5 overflow-x-auto" data-testid="progress-steps">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-3 shrink-0">
          <span className={`step-dot ${i < current ? "done" : i === current ? "current" : ""}`}>{i + 1}</span>
          <span className={`text-xs ${i <= current ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
          {i < steps.length - 1 && <span className="w-6 h-px bg-[hsl(var(--border))]"/>}
        </li>
      ))}
    </ol>
  );
}

function PayModal({ delegationId, onClose, onPaid }) {
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/delegations/${delegationId}/quote`); setQuote(data); }
      catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [delegationId]);

  const submit = async (receipt) => {
    setErr("");
    try {
      await api.post(`/delegations/${delegationId}/pay`, receipt);
      onPaid();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      throw e;
    }
  };

  return (
    <Modal onClose={onClose} testid="pay-modal">
      <div className="label-eyebrow text-[hsl(var(--primary))]">Pago Protegido · Transferencia manual</div>
      <h2 className="serif text-3xl mt-2">Confirmar Pago Protegido</h2>
      {!quote ? <div className="mt-6 text-muted-foreground">Cargando cotización…</div> : (
        <>
          <TotalOnlyBlock quote={quote} />
          {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="pay-modal-err">{err}</div>}
          <ManualTransferForm
            amount={quote.total_clp}
            referenceLabel="Delegación · Pago Protegido"
            submitLabel={`Enviar comprobante (${formatCLP(quote.total_clp)})`}
            onCancel={onClose}
            onSubmit={submit}
            kind="delegation"
          />
        </>
      )}
    </Modal>
  );
}

function ApproveModal({ delegationId, onClose, onApproved }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const canSubmit = rating >= 1 && rating <= 5 && comment.trim().length >= 5;

  const approve = async () => {
    if (!canSubmit) { setErr("Debes seleccionar 1 a 5 estrellas y escribir al menos 5 caracteres."); return; }
    setSaving(true); setErr("");
    try { await api.post(`/delegations/${delegationId}/approve`, { rating, comment: comment.trim() }); onApproved(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testid="approve-modal">
      <div className="label-eyebrow text-[hsl(var(--accent))]">Liberación de pago</div>
      <h2 className="serif text-3xl mt-2">Aprobar gestión</h2>
      <p className="mt-3 text-sm text-muted-foreground">
        Califica el desempeño del colega. Esto libera su pago y publica una reseña.
      </p>
      <p className="mt-3 p-3 rounded-md border border-cyan-400/25 bg-cyan-400/[0.05] text-xs text-cyan-100" data-testid="approve-mandatory-note">
        Tu calificación ayuda a que otros colegas confíen y mantiene la calidad de ColegaLink.
      </p>
      <div className="mt-5">
        <div className="label-eyebrow mb-2">Calificación · obligatoria *</div>
        <div className="flex items-center gap-2">
          {[1,2,3,4,5].map((n) => (
            <button type="button" key={n} onClick={()=>setRating(n)}
              className={`w-10 h-10 rounded-md inline-flex items-center justify-center border ${n <= rating ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-transparent" : "border-[hsl(var(--border))] text-muted-foreground"}`}
              data-testid={`approve-rating-${n}`}><Star size={14}/></button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <label className="label-eyebrow">Comentario · obligatorio *</label>
        <textarea
          className="field mt-2"
          rows={3}
          placeholder="Cuenta cómo fue la experiencia (mínimo 5 caracteres)."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          required
          minLength={5}
          data-testid="approve-comment"
        />
        <div className="mt-1 text-[10px] text-muted-foreground text-right">{comment.trim().length}/5+ caracteres</div>
      </div>
      {err && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="approve-err">{err}</div>}
      <div className="mt-6 flex gap-3">
        <button onClick={approve} disabled={saving || !canSubmit} className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="confirm-approve">
          <CheckCircle2 size={16} className="mr-2"/>{saving ? "Aprobando…" : "Aprobar y liberar pago"}
        </button>
        <button onClick={onClose} className="btn-ghost" disabled={saving}>Cancelar</button>
      </div>
    </Modal>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur overflow-y-auto" data-testid={testid}>
      <div className="panel p-8 max-w-md w-full relative max-h-[90vh] overflow-y-auto my-4">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" aria-label="Cerrar"><X size={18}/></button>
        {children}
      </div>
    </div>
  );
}

// Bloque de pago del solicitante: sólo muestra el TOTAL FINAL en grande.
// El desglose (honorario del colega + cargo Colega Link + notas) queda
// oculto detrás de un "Ver detalle del cobro" — el mandante decide si lo abre.
function TotalOnlyBlock({ quote }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  return (
    <div className="mt-6" data-testid="pay-modal-total-only">
      <div className="p-6 rounded-lg bg-gradient-to-br from-amber-950/30 via-slate-950/60 to-slate-950 border border-amber-400/25 text-center">
        <div className="label-eyebrow text-amber-300">Total final a pagar</div>
        <div className="serif text-5xl brand-gradient-text mt-2" data-testid="pay-modal-total">
          {formatCLP(quote.total_clp)}
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">
          IVA e impuestos incluidos · el colega recibe {formatCLP(quote.fee_clp)} al aprobar el trabajo
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowBreakdown((v) => !v)}
        className="mt-3 text-xs text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-4 mx-auto block"
        data-testid="pay-modal-toggle-breakdown"
      >
        {showBreakdown ? "Ocultar detalle del cobro" : "Ver detalle del cobro"}
      </button>
      {showBreakdown && (
        <ul className="mt-3 space-y-2 text-xs text-muted-foreground p-3 rounded-md bg-white/[0.03] border border-white/5" data-testid="pay-modal-breakdown">
          <li className="flex justify-between"><span>Honorario del colega</span><span className="font-medium text-foreground">{formatCLP(quote.fee_clp)}</span></li>
          <li className="flex justify-between"><span>Cargo Colega Link ({quote.commission_pct}%)</span><span className="font-medium text-foreground">{formatCLP(quote.commission_clp)}</span></li>
          <li className="pt-2 border-t border-white/5 flex justify-between"><span className="text-foreground">Total</span><span className="font-semibold text-amber-300">{formatCLP(quote.total_clp)}</span></li>
          {(quote.commission_min_applied || quote.commission_reference_applied) && (
            <li className="text-amber-200/90 p-2 rounded-md bg-amber-400/10 border border-amber-400/25">
              {quote.commission_min_applied && !quote.commission_reference_applied && (
                <>Se aplicó el <strong>cargo mínimo Colega Link ({formatCLP(quote.commission_min_clp)})</strong> porque el {quote.commission_pct}% del honorario resultaba menor.</>
              )}
              {quote.commission_reference_applied && !quote.commission_min_applied && (
                <>El cargo se calcula sobre la <strong>tarifa referencial de mercado ({formatCLP(quote.commission_reference_clp)})</strong>, no sobre el honorario declarado.</>
              )}
              {quote.commission_min_applied && quote.commission_reference_applied && (
                <>Se aplicó el <strong>cargo mínimo Colega Link ({formatCLP(quote.commission_min_clp)})</strong> — la tarifa referencial ({formatCLP(quote.commission_reference_clp)}) generaba un cargo menor.</>
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function FilesSection({ delegation: d, reload, role }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-3xl">Documentos del caso</h2>
      <p className="text-sm text-muted-foreground mt-1">Solo visibles para el titular y el colega asignado.</p>

      {/* Owner can upload case + power; both can view */}
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Antecedentes del caso</div>
            <span className="pill">{(d.case_files || []).filter(f => f.kind === "case").length} arch.</span>
          </div>
          <FileList files={(d.case_files || []).filter(f => f.kind === "case")} delegationId={d.id}/>
          {role === "titular" && <FileUploader delegationId={d.id} kind="case" onUploaded={reload}/>}
        </div>
        <div className="panel p-5">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Delega poder firmado</div>
            <span className="pill">{(d.case_files || []).filter(f => f.kind === "power").length} arch.</span>
          </div>
          <FileList files={(d.case_files || []).filter(f => f.kind === "power")} delegationId={d.id}/>
          {role === "titular" && <FileUploader delegationId={d.id} kind="power" onUploaded={reload}/>}
        </div>
      </div>

      {(d.proof_files || []).length > 0 && (
        <div className="panel p-5 mt-6">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Comprobante de gestión</div>
            <span className="pill badge-verified">{d.proof_files.length} arch.</span>
          </div>
          <FileList files={d.proof_files} delegationId={d.id}/>
        </div>
      )}
    </section>
  );
}

function FileList({ files, delegationId }) {
  if (!files || files.length === 0) return <p className="mt-4 text-sm text-muted-foreground">Sin archivos.</p>;
  return (
    <ul className="mt-4 space-y-2" data-testid={`file-list-${delegationId}`}>
      {files.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`file-${f.id}`}>
          <span className="inline-flex items-center gap-2 truncate"><FileText size={14} className="text-[hsl(var(--primary))] shrink-0"/>{f.filename}</span>
          <button onClick={() => downloadFile(delegationId, f)} className="text-xs link-underline inline-flex items-center gap-1" data-testid={`download-${f.id}`}><Download size={12}/>Descargar</button>
        </li>
      ))}
    </ul>
  );
}

async function downloadFile(delegationId, file) {
  try {
    const { data } = await api.get(`/delegations/${delegationId}/files/${file.id}`);
    const raw = data.file.data_b64;
    const byteChars = atob(raw);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    const blob = new Blob([bytes], { type: file.mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = file.filename; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
}

function FileUploader({ delegationId, kind, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Archivo demasiado grande (máx 5MB)"); return; }
    setBusy(true); setErr("");
    try {
      const data_b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",")[1]);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      await api.post(`/delegations/${delegationId}/files`, {
        filename: file.name, mime: file.type || "application/octet-stream",
        data_b64, kind,
      });
      onUploaded();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handle(f);
  };

  return (
    <div className="mt-4">
      <label
        className={`drop-zone block transition ${dragOver ? "ring-2 ring-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/5" : ""}`}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`uploader-${kind}`}
      >
        <Upload size={20} className="mx-auto text-[hsl(var(--primary))]"/>
        <div className="serif text-lg mt-3">{busy ? "Subiendo…" : dragOver ? "Suelta para subir" : "Arrastra o haz clic para subir"}</div>
        <p className="text-xs text-muted-foreground mt-1">PDF, JPG o PNG · máx 5MB</p>
        <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden"
          onChange={(e) => handle(e.target.files?.[0])} disabled={busy}/>
      </label>
      {err && <div className="mt-2 text-sm text-[hsl(var(--destructive))]">{err}</div>}
    </div>
  );
}

function ProofUpload({ delegationId, onUploaded }) {
  return (
    <div className="mt-5">
      <FileUploader delegationId={delegationId} kind="proof" onUploaded={onUploaded}/>
    </div>
  );
}
